import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminService, type AdminUser } from '@/services/admin-service';
import { sessionAuthService } from '@/services/session-auth-service';
import { 
  Header, 
  Field,
  FieldLabel,
  CheckboxField,
  ContentContainer,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  PaginationEllipsis,
  Empty,
  EmptyIcon,
  EmptyTitle,
  EmptyDescription,
  Spinner,
  SearchInput,
  Button,
  Badge,
  Alert,
  AlertDescription,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  toast,
} from 'prosci-ui';
import { usePageContext } from '@/contexts/page-context';
import { 
  Plus, 
  Edit, 
  Trash2, 
  AlertCircle,
  MoreHorizontal,
  UserRoundX
} from '@/lib/icons';
import { format } from 'date-fns';
import { AdminUserCreateDialog } from './admin-user-create-dialog';
import { AdminUserEditDialog } from './admin-user-edit-dialog';

export function AdminUsersPage() {
  const { setShowPageHeader } = usePageContext();
  const [searchQuery, setSearchQuery] = useState('');
  const [includeInactive, setIncludeInactive] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [userToDelete, setUserToDelete] = useState<AdminUser | null>(null);
  
  const queryClient = useQueryClient();
  const currentAdmin = sessionAuthService.getAdminInfo();
  const pageSize = 20;

  useEffect(() => {
    setShowPageHeader(false);
    return () => setShowPageHeader(true);
  }, [setShowPageHeader]);

  // Check if current user is super admin
  const isCurrentUserSuper = currentAdmin?.permissions?.includes('*') || false;
  
  // Convert session admin to AdminUser format for canEditUser function
  const currentAdminUser: AdminUser | null = currentAdmin ? {
    id: currentAdmin.id,
    email: currentAdmin.email,
    name: currentAdmin.name,
    is_super: isCurrentUserSuper,
    // These fields aren't in the session, but are required by the type
    first_name: currentAdmin.name.split(' ')[0] || '',
    last_name: currentAdmin.name.split(' ').slice(1).join(' ') || '',
    role: (isCurrentUserSuper ? 'super' : 'admin') as 'super' | 'admin',
    status: 'active',
    created_at: '',
    updated_at: '',
  } : null;

  // Fetch admin users
  const { data, isLoading, error } = useQuery({
    queryKey: ['adminUsers', searchQuery, includeInactive, currentPage],
    queryFn: () => adminService.listAdmins({
      q: searchQuery,
      include_inactive: includeInactive,
      limit: pageSize,
      skip: currentPage * pageSize
    }),
    staleTime: 30000, // 30 seconds
  });

  // Extract users and pagination info
  const users = data?.data?.map(item => ({
    ...item.attributes,
    id: item.id
  })) || [];
  const totalCount = data?.meta?.total_count || 0;
  const totalPages = Math.ceil(totalCount / pageSize);

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => adminService.deleteAdmin(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminUsers'] });
    },
  });


  const handleDelete = (user: AdminUser) => {
    if (user.id === currentAdmin?.id) {
      toast.error("You cannot delete your own account.");
      return;
    }
    setUserToDelete(user);
  };

  const confirmDelete = async () => {
    if (!userToDelete) return;
    
    try {
      await deleteMutation.mutateAsync(userToDelete.id);
      toast.success(`${userToDelete.name} has been deleted successfully.`);
      setUserToDelete(null);
    } catch (error) {
      console.error('Failed to delete admin user:', error);
      toast.error(
        error instanceof Error 
          ? `Failed to delete admin user: ${error.message}` 
          : 'Failed to delete admin user. Please try again.'
      );
    }
  };

  const handleEdit = (user: AdminUser) => {
    setSelectedUser(user);
    setShowEditDialog(true);
  };

  const handleCreate = () => {
    setShowCreateDialog(true);
  };

  const clearFilters = () => {
    setSearchQuery('');
    setIncludeInactive(false);
    setCurrentPage(0);
  };

  const hasFilters = Boolean(searchQuery || includeInactive);

  if (error) {
    return (
      <div className="p-6">
        <Alert variant="destructive">
          <AlertCircle className="h-5 w-5" />
          <AlertDescription>Failed to load admin users. Please try again.</AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <>
      <Header 
        title="Admin Users" 
        size="small"
      >
        <Button onClick={handleCreate} variant="lilac">
          <Plus className="h-4 w-4" />
          Add Admin User
        </Button>
      </Header>
      
      {/* Filters */}
      <div className="bg-white border-b border-accent-sand">
        <div className="max-w-[1160px] px-6 py-4 mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Search Users */}
            <SearchInput
              id="search"
              label="Search Admin Users"
              placeholder="Search by name, email, or organization..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(0);
              }}
            />

            {/* Include Inactive Filter */}
            <Field>
              <FieldLabel>Filter</FieldLabel>
              <CheckboxField
                checked={includeInactive}
                onCheckedChange={(checked) => {
                  setIncludeInactive(!!checked);
                  setCurrentPage(0);
                }}
                label="Include inactive users"
              />
            </Field>
          </div>
          
          {hasFilters && (
            <div className="flex items-center justify-end mt-4">
              <Button
                type="button"
                variant="ghost"
                onClick={clearFilters}
              >
                Clear Filters
              </Button>
            </div>
          )}
        </div>
      </div>

      <ContentContainer>
        {/* Users table */}
        {isLoading ? (
          <Empty>
            <EmptyIcon>
              <Spinner size="small" />
            </EmptyIcon>
            <EmptyTitle>Loading admin users</EmptyTitle>
            <EmptyDescription>
              Please wait while we fetch the admin users...
            </EmptyDescription>
          </Empty>
        ) : users.length === 0 ? (
          <Empty>
            <EmptyIcon>
              <UserRoundX />
            </EmptyIcon>
            <EmptyTitle>No admin users found</EmptyTitle>
            <EmptyDescription>
              {searchQuery ? 'Try adjusting your search to find admin users.' : 'No admin users have been created yet.'}
            </EmptyDescription>
            {hasFilters && (
              <Button
                type="button"
                variant="outline"
                onClick={clearFilters}
              >
                Clear Filters
              </Button>
            )}
          </Empty>
        ) : (
          <Card>
            <CardHeader>
              <CardTitle>Admin Users</CardTitle>
              <CardDescription>
                Complete list of all admin users in your organization.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="text-right">
                  <span className="sr-only">Actions</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((user) => {
                const canEdit = adminService.canEditUser(
                  currentAdminUser, 
                  user
                );
                const canDelete = canEdit && user.id !== currentAdmin?.id;

                return (
                  <TableRow key={user.id}>
                    <TableCell>
                      <div className="text-sm font-medium">
                        {user.name}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {user.email}
                      </div>
                      {user.affiliate_name && (
                        <div className="text-xs text-muted-foreground">
                          {user.affiliate_name}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      {adminService.getRoleDisplayName(user.role)}
                    </TableCell>
                    <TableCell>
                      {user.status === 'active' ? (
                        <Badge variant="success" tint>
                          Active
                        </Badge>
                      ) : (
                        <Badge variant="default" tint>
                          Inactive
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {user.created_at ? format(new Date(user.created_at), 'MMM d, yyyy') : '-'}
                    </TableCell>
                    <TableCell className="text-right">
                      {(canEdit || canDelete) && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreHorizontal />
                              <span className="sr-only">Open menu</span>
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {canEdit && (
                              <DropdownMenuItem onClick={() => handleEdit(user)}>
                                <Edit />
                                Edit User
                              </DropdownMenuItem>
                            )}
                            {canEdit && canDelete && <DropdownMenuSeparator />}
                            {canDelete && (
                              <DropdownMenuItem 
                                variant="destructive"
                                onClick={() => handleDelete(user)}
                              >
                                <Trash2 />
                                Delete
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
              </TableBody>
              </Table>
            </CardContent>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="mt-6 border-t pt-4 flex flex-col md:flex-row items-center justify-between gap-8">
                  <p className="text-sm text-gray-600">
                    Showing {currentPage * pageSize + 1} to {Math.min((currentPage + 1) * pageSize, totalCount)} of {totalCount} users
                  </p>
                  <Pagination className="w-auto mx-0">
                    <PaginationContent>
                      <PaginationItem>
                        <PaginationPrevious
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            setCurrentPage(p => Math.max(0, p - 1));
                          }}
                          className={currentPage === 0 ? 'pointer-events-none opacity-50' : ''}
                        />
                      </PaginationItem>
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        let pageNum;
                        if (totalPages <= 5) {
                          pageNum = i;
                        } else if (currentPage <= 2) {
                          pageNum = i;
                        } else if (currentPage >= totalPages - 3) {
                          pageNum = totalPages - 5 + i;
                        } else {
                          pageNum = currentPage - 2 + i;
                        }
                        
                        return (
                          <PaginationItem key={pageNum}>
                            <PaginationLink
                              href="#"
                              onClick={(e) => {
                                e.preventDefault();
                                setCurrentPage(pageNum);
                              }}
                              isActive={pageNum === currentPage}
                            >
                              {pageNum + 1}
                            </PaginationLink>
                          </PaginationItem>
                        );
                      })}
                      {totalPages > 5 && currentPage < totalPages - 3 && (
                        <PaginationItem>
                          <PaginationEllipsis />
                        </PaginationItem>
                      )}
                      <PaginationItem>
                        <PaginationNext
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            setCurrentPage(p => Math.min(totalPages - 1, p + 1));
                          }}
                          className={currentPage === totalPages - 1 ? 'pointer-events-none opacity-50' : ''}
                        />
                      </PaginationItem>
                    </PaginationContent>
                  </Pagination>
                </div>
              )}
          </Card>
        )}

      {/* Dialogs */}
      {showCreateDialog && (
        <AdminUserCreateDialog 
          onClose={() => setShowCreateDialog(false)}
          onSuccess={() => {
            setShowCreateDialog(false);
            queryClient.invalidateQueries({ queryKey: ['adminUsers'] });
          }}
          canCreateSuper={isCurrentUserSuper}
        />
      )}
      
      {showEditDialog && selectedUser && (
        <AdminUserEditDialog
          user={selectedUser}
          onClose={() => {
            setShowEditDialog(false);
            setSelectedUser(null);
          }}
          onSuccess={() => {
            setShowEditDialog(false);
            setSelectedUser(null);
            queryClient.invalidateQueries({ queryKey: ['adminUsers'] });
          }}
          canEditSuper={isCurrentUserSuper}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!userToDelete} onOpenChange={(open) => !open && setUserToDelete(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Admin User</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete <strong>{userToDelete?.name}</strong>? This action cannot be undone and will permanently remove their access to the admin portal.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              onClick={() => setUserToDelete(null)}
              variant="outline"
            >
              Cancel
            </Button>
            <Button
              onClick={confirmDelete}
              variant="destructive"
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete Admin User'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </ContentContainer>
    </>
  );
}